import PropTypes from 'prop-types';
import { useState, useCallback, useEffect } from 'react';
// @mui
import { Box, Typography, Stack, LinearProgress, CircularProgress, ListItemText, Link } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { RouterLink } from 'src/routes/components';
// utils
import { formatMB, getStorageColor } from 'src/utils/formatFileSize';
import axiosInstance, { endpoints } from 'src/utils/axios';
//
import { StyledItem, StyledIcon } from './styles';

// ----------------------------------------------------------------------

export default function NavStorageWidget({ item, active, config, disabled }) {
    const theme = useTheme();
    const { title, path, icon } = item;

    const [loading, setLoading] = useState(true);
    const [percentUsed, setPercentUsed] = useState(0);
    const [usedFormatted, setUsedFormatted] = useState('0 MB');
    const [totalFormatted, setTotalFormatted] = useState('0 MB');
    const [retryCount, setRetryCount] = useState(0);

    const fetchStorageData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.post(endpoints.storage.analytics, {}, { _skipRedirectOn403: true });
            if (response.data?.success && response.data?.data?.storage) {
                const { used, totalLimit, percentUsed: pct } = response.data.data.storage;


                let calculatedPct;
                if (typeof pct === 'number' && !Number.isNaN(pct)) {
                    calculatedPct = pct;
                } else if (totalLimit > 0) {
                    calculatedPct = (used / totalLimit) * 100;
                } else {
                    calculatedPct = 0;
                }
                setPercentUsed(Math.min(calculatedPct || 0, 100));
                setUsedFormatted(formatMB(used, 2));
                setTotalFormatted(formatMB(totalLimit, 2));
            }
        } catch (error) {
            console.error('Failed to fetch storage overview for sidebar', error);
            if (retryCount < 3) {
                setTimeout(() => {
                    setRetryCount((prev) => prev + 1);
                }, 5000);
            }
        } finally {
            setLoading(false);
        }
    }, [retryCount]);

    useEffect(() => {
        fetchStorageData();
    }, [fetchStorageData]);

    const progressColor = getStorageColor(percentUsed);

    const renderContent = (
        <StyledItem
            disableGutters
            disabled={disabled}
            active={active}
            depth={1}
            config={config}
            sx={{
                flexDirection: 'column',
                alignItems: 'stretch',
                py: 1.5,
                height: 'auto',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
                {icon && <StyledIcon size={config.iconSize}>{icon}</StyledIcon>}

                <ListItemText
                    primary={title}
                    primaryTypographyProps={{
                        noWrap: true,
                        typography: 'body2',
                        textTransform: 'capitalize',
                        fontWeight: active ? 'fontWeightSemiBold' : 'fontWeightMedium',
                    }}
                />

                {loading && (
                    <CircularProgress size={14} sx={{ ml: 'auto', color: 'text.secondary' }} />
                )}
            </Box>

            <Box sx={{ width: '100%', pl: icon ? 0 : 0 }}>
                <LinearProgress
                    variant="determinate"
                    value={percentUsed}
                    color={progressColor}
                    sx={{
                        height: 6,
                        borderRadius: 1,
                        mb: 1,
                        backgroundColor: theme.palette.action.focus
                    }}
                />

                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', textAlign: 'left' }}>
                    {loading ? 'Calculating...' : `${usedFormatted} of ${totalFormatted} used`}
                </Typography>
            </Box>
        </StyledItem>
    );

    return (
        <Link
            component={RouterLink}
            href={path}
            underline="none"
            color="inherit"
            sx={{
                ...(disabled && { cursor: 'default', pointerEvents: 'none' })
            }}
        >
            {renderContent}
        </Link>
    );
}

NavStorageWidget.propTypes = {
    item: PropTypes.object,
    active: PropTypes.bool,
    config: PropTypes.object,
    disabled: PropTypes.bool,
};
